// Feeling → example sample images (Feeling system · Phase 3 plumbing, 2026-07-09).
//
// Maps a feeling NAME → an array of directly-fetchable sample-card image URLs shown as thumbnails in
// the Feeling step (and enlarged in the lightbox). Every feeling in feelingTaxonomy.js now carries
// THREE curated examples. All were generated via the live store (real gpt-image-2 pipeline + each
// feeling's own art-direction), then each generation was DETERMINISTICALLY mapped back to its feeling
// from the card's own provenance.inputs.feeling — no guessing. Every URL is a stable, publicly-served,
// non-expiring /uploads/cards/…/preview.webp?v= cover.
//
// The UI MUST degrade gracefully: a feeling with NO samples shows a calm placeholder tile (never a
// broken image), and CardCoverImage falls back to the dignified placeholder if a listed URL fails.
// Keep the keys EXACTLY equal to the feeling names in feelingTaxonomy.js.

const BASE = 'https://api.imobituary.com/uploads/cards';
// Compact table → URL. Each entry is [cardId, imageId, versionToken]; s() builds the served cover URL.
const s = (card, img, v) => `${BASE}/${card}/images/${img}/preview.webp?v=${v}`;

export const FEELING_SAMPLES = {
  // ── Tender ────────────────────────────────────────────────────────────────
  'Comforting': [
    s('cc0f38f8-36e2-4fc3-8a19-df2ff1d995da', '31102c76-e974-4cd9-a258-878a695bb22f', '1783708874330'),
    s('684c4ece-07d8-4da6-a789-6ca2f9def6fd', '4c04dd9a-073f-4eb7-8cc9-5a056bfa5713', '1783733274130'),
    s('d42c4dab-b32d-4841-9d0c-2b31aaf21d81', '9cbfa4e5-38e7-45f8-afac-b2f607bc8117', '1783738526093'),
  ],
  'Loving': [
    s('c7b19cd7-f23e-4505-aa48-10d00c827b41', '5f74ac00-aa8c-4a58-a3b1-7d9475a4de27', '1783731727472'),
    s('3da2e733-99db-43ff-9442-f2894b8752a0', 'c2632e5f-e586-40c8-a7a4-2184fdac5651', '1783738530560'),
    s('1122b36d-dcc0-47e7-b91e-b1b8636a3daf', 'ecae3ced-5fc4-418f-9d1d-2e9748967b64', '1783738532593'),
  ],
  'Grateful': [
    s('58a2e841-fa61-4315-9e3b-82f06c453ffc', '0ea0dcda-b291-41d5-80cf-008470b3983e', '1783731777413'),
    s('f7a5882d-610a-4f9c-8405-c9c50a8fc5ba', 'f297a2d1-4d54-43c7-807d-b7c055f6914d', '1783738534734'),
    s('3c609486-6370-43fd-bd93-8addf4c6acb9', '872be272-d4bc-44c0-af52-f0179a27744b', '1783738536816'),
  ],
  'Hopeful': [
    s('47b4010d-ead9-4634-94c7-0f5b2e55293a', '02c6e23e-dc60-4f1c-b32d-b7ae1bdcc5ed', '1783731887155'),
    s('3a5ab2e3-f50e-414c-833a-55cc5204b70f', '1ebe3db0-97bf-4bd0-bd55-edcf87380f5c', '1783738823586'),
    s('f0a8f041-0914-4c82-bce3-17ebeec2ff15', '5a46e8bc-c7fc-47b8-b187-a0012586c590', '1783738825624'),
  ],
  'Peaceful': [
    s('31267e87-ceb0-42c2-beb0-e15ca7b901e6', '05ad7288-4c49-4cb2-9467-c257cac1d277', 'b8ef4fbaeb6c9acc4224ac9acc8eabd9'),
    s('79591c07-9c08-4ae1-b279-23235c5f8627', 'a08e92a0-ad11-4793-9085-2c92deee02ff', '1783738827412'),
    s('a29dbb54-35f3-4f65-8150-2c2631487916', 'afba4804-c53d-4ca2-914a-d2e4f5ccce20', '1783738829375'),
  ],
  // ── Reverent ──────────────────────────────────────────────────────────────
  'Faithful': [
    s('0003e7c7-9adf-4b1d-bc3b-86b68506410e', '629218d5-cdc2-41fd-aa35-7bda19ce4bdb', '1783733425760'),
    s('e6a76c95-7750-44f5-90f7-c40bf9df180f', 'aa51585a-26f7-4c71-84ed-ec0c9ef217b0', '1783738539619'),
    s('601b0849-e9a7-4b74-9a65-f78bfdf9c4e1', 'f254b6d6-1a27-4575-b4af-6ba431dc2480', '1783738541812'),
  ],
  'Reverent': [
    s('916e8f7a-4335-445c-b6ad-db3fc098907c', '585d9071-3b3a-4a99-8e05-742e0eba10d0', '1783733557660'),
    s('10f15a27-0ed8-46a1-858e-b72f544bc706', '127b345d-7fbf-4216-875d-bc3db1917aff', '1783738544162'),
    s('27c96ab1-ec13-471e-aca9-2cae381ceaff', '631ae192-e04a-49c7-8626-b31493292b06', '1783738546336'),
  ],
  'Reflective': [
    s('fb0a3096-e0f2-4a30-9231-16c5ada296ea', '62cfd7c9-2f3c-4d2d-bfc5-b845d2cae71a', '1783734403433'),
    s('bd6cd6c4-0877-4cee-abcf-5457759b0c5e', '76e605de-c5bd-4c15-be13-eb87121b66b1', '1783738831299'),
    s('cc3a65f4-972c-40b7-be5f-dc6f31533aab', 'c8ad00f4-071b-460f-bab5-afab37ad1d64', '1783739231682'),
  ],
  'Honoring': [
    s('3ee8a934-e72b-4215-ac52-b4ef48214745', '7bcf3e08-b554-4c9d-b291-27c433415033', '1783734441565'),
    s('f68099a0-b085-410c-8ac2-8a5b0dfac816', '317b03e0-09c8-4a57-875d-4e31ac1aaa1a', '1783738846225'),
    s('a6ef57a7-2434-470f-a064-d5755dfa672d', 'e9cfbcf2-d94c-4313-bfe7-f16f2fc7497b', '1783739233651'),
  ],
  // ── Unfiltered ────────────────────────────────────────────────────────────
  'Honest and raw': [
    s('b8046ba1-705b-4d47-ba92-7424597f8c87', 'cd31b261-1ff1-4656-91a9-7e32afea8d6c', '1783735702086'),
    s('fb40c1f1-8d8d-4dab-93ed-865fa518dc88', 'b94f6af8-bebc-4731-8822-89942f45ee29', '1783739235693'),
    s('1236a1ce-b676-49f8-8b57-35177d3d6446', 'da88e1f7-3267-42a2-815d-73c98ce36c6a', '1783739237754'),
  ],
  'Bittersweet': [
    s('f7f84fce-563e-4419-9c23-5c69d26bce41', '39f5cc77-262e-4a91-8c62-6f8fcef3e049', '1783735701868'),
    s('da2e47b7-9ea8-4272-bb0b-bb133db84865', 'bcbd8e1a-ded3-4119-bc1b-40eb7db91208', '1783739031565'),
    s('741ef651-b574-4d27-9f77-2cea9afba2d2', '09bfb4a7-a4cc-466b-8dc7-1f3d1cf5f261', '1783739033454'),
  ],
  'Nostalgic': [
    s('cd0ae454-ce58-411a-a3e9-c4bceff1fbc6', '793640cb-6e90-4201-8d6f-f6606f6cf80d', '1783735692702'),
    s('600e732d-1faf-4020-9768-991c9eb280ca', 'e1b31ace-f614-4c69-8514-8409c6ccdabf', '1783738549532'),
    s('5ffcf3c7-054e-4a7b-927b-49341c2cd226', 'ae125ffb-a7d7-44fe-b0af-2e079cd3d6d2', '1783739035469'),
  ],
  'Wistful': [
    s('77a4e819-69e2-4367-9fde-8134770c0a9c', '37d8a4d6-952f-4ea5-a293-e8a05ddacda8', '1783735689714'),
    s('c9584669-ede5-46cb-923b-2b4a325918d9', '2388e208-287d-48c6-8a39-e6387d3f3e37', '1783738551592'),
    s('53ba399b-4602-47ed-8efb-7dfdd6f7a879', '06b54f05-5612-4168-83d7-af222d7f4afb', '1783739037356'),
  ],
  // ── Joyful ────────────────────────────────────────────────────────────────
  'Celebratory': [
    s('b106f7b6-74b8-4936-82f2-d9807f1b9d69', 'f3722279-4a90-4851-8c1d-12efeaabbc1e', '1783735692792'),
    s('13cfca88-97be-4349-b410-e605b65843e3', '7fbe465f-c40d-4894-8a8a-33c408415cd2', '1783739039365'),
    s('13364cd4-f6a5-4f94-8e4f-a0252a27ce27', 'e2ef9333-495e-4c47-b0f0-6eab4b643a50', '1783739041185'),
  ],
  'Joyful': [
    s('1f34818a-32b4-471a-98b2-8418b0ebd91d', '04659221-3833-4ee9-a8f7-241aa38a33d0', '1783735692580'),
    s('d362e2bf-5757-4c9b-9933-2bf889a3a12c', '4bc46ea7-fbbb-437a-a2e8-fba701762cfd', '1783739239735'),
    s('fa316c76-1433-40e9-ad04-c7641957669f', 'e9cf8800-3f95-4c46-83bf-8df09647c813', '1783739241600'),
  ],
  'Festive': [
    s('c4066f02-d408-48ad-ab65-6e4a270b382d', 'b043f11e-70c4-44f9-bec9-db3b99939d7a', '1783735701937'),
    s('74de9886-8d9c-4723-9dbb-cfcb2794e9fb', '858db986-9040-4c65-b9e8-0f48296a206e', '1783738554146'),
    s('d0e0d6d5-65ef-41ba-81bf-908bb569bb51', '74363145-2b11-46b8-b80a-6b322445284c', '1783739410898'),
  ],
  'Radiant': [
    s('bb60ba29-51eb-40e7-83c9-1cd1ba902196', '084fb7c9-1000-4885-a3d0-091189fb08cc', '1783735692547'),
    s('4e27e20f-e4db-4632-aef6-b9a144e14c3b', '62d30da1-79b1-4657-a7a1-4892ae49eba9', '1783739412911'),
    s('be5f5dfb-0a01-48c7-b146-6eaab4403cea', '4a2cc362-cb5b-4b6a-8477-15b1d141f664', '1783739414841'),
  ],
  // ── Playful ───────────────────────────────────────────────────────────────
  'Funny': [
    s('905f47b3-6a87-4514-a0ae-60a3fab031f9', '77f8cee6-af10-434c-94eb-fc56802e30a4', '1783729845972'),
    s('04966e3a-bb55-4f23-b89d-93077bfc4d4b', '2e4dae5e-40b4-4a3e-a9d5-2879b8bf466e', '1783739416993'),
    s('34351de2-00e0-4a76-b9ea-33a829d48e14', 'c8abfb82-d901-4853-a211-f7ca724076cc', '1783739419643'),
  ],
  'Light-hearted': [
    s('5a83d11b-b289-4d23-9a3b-07fc1e7900f4', '65d1ddc5-7d72-4d2d-b796-99b021a40fa1', '1783731059264'),
    s('af2f89d5-f550-42ac-a7c2-a666d1e53851', '2f5b2a0d-d353-4ef2-923c-b1a43024b389', '1783738556955'),
    s('f6263c5b-a2c2-4ed3-9865-107daf322cc7', '0266043c-3e10-423c-b36f-4073d6fdebc1', '1783739424509'),
  ],
  'Playful': [
    s('44d93855-04b9-4504-846e-07abea956295', 'edc2cdce-d982-4e03-92ae-e02b2d08aaba', '1783730723151'),
    s('b85be573-4a7a-42e2-9ffc-1dd7658f9a92', 'f250046d-b16f-447f-bd33-a729711938a2', '1783739596463'),
    s('59993134-c069-4745-b2bd-6d35ae18f88b', 'b02a140a-90fa-4c6c-ae31-7a4b6a0fa11c', '1783739598378'),
  ],
  'Cheeky': [
    s('999d2406-e883-49a8-b0b9-6e4376924820', 'c3031842-f149-48a8-b413-138295256a29', '1783730825658'),
    s('c706c541-9504-404f-a467-be4c2d995392', 'e677cffd-b546-4735-bbd1-52f758470963', '1783739600397'),
    s('08a8b8b2-a15c-4454-9f4b-2777d3b7a0d9', 'd451d26d-434c-4395-833b-cc7d2055b9e7', '1783739602560'),
  ],
  'Irreverent': [
    s('4524505a-7c89-4162-ae73-7a39f928610f', 'b21f5ac3-8cdf-43ae-9955-23e354b08963', '1783730941439'),
    s('76d4ad73-d72c-4195-af52-010e8b035ae8', '21f97365-08e3-4931-a0df-1de00d8e0780', '1783739604645'),
    s('addf77db-c933-49b3-bc25-5fcb353e4945', '49becd0c-c94e-4e9a-847c-fcafd686d867', '1783739606638'),
  ],
  'Absurd': [
    s('3725ecc7-0888-4f8d-86b6-a1139821662c', '035f5049-9932-4714-b4b6-eeae6b246423', '1783726444666'),
    s('95d2444b-2bf2-42e4-86c6-94901e617c3f', '2df9cc7b-c468-43a0-9e1c-6fa65bebba66', '1783738559612'),
    s('48956fa7-7c0d-4a77-9654-5a579ce4e780', 'ff990a15-de77-4737-b327-b5287bb20691', '1783739789982'),
  ],
  // ── Bold ──────────────────────────────────────────────────────────────────
  'Epic': [
    s('e252f8c2-9206-44c2-aa49-fb742e4e85ec', '5b6607d7-dc9a-4b01-9a06-d60b3098af3a', 'e6e1c40847089278d120410654331ae6'),
    s('d8519e16-517f-48f1-a532-f5851a717f2f', '46863012-a87a-4c90-b6dc-ba16a8369518', 'c775db6aa0a5f988a49d84f75375e876'),
    s('e36dd30e-ebda-4952-983a-26cb93e24b14', '4f5011c5-b7da-4c2e-aad0-2064cb1ec806', '452e264c649a2b63e1683ea948c15afe'),
  ],
  'Heroic': [
    s('a6dc353c-68ea-4d30-a849-a7fb34f5c1b7', '460f0242-52e6-4179-a57c-558be99b4566', '1783731887123'),
    s('0164e4ab-ef94-4d70-a503-c36c03b8e93a', '1f6a1b92-2885-4a2e-9ec0-bf6879ce5e97', '1783739608709'),
    s('58f6edfe-7444-4512-a8da-7f67a7b2928a', 'ca56b586-6294-4777-9b17-bc6b3615aa7e', '1783739791916'),
  ],
  'Triumphant': [
    s('81790829-fc5f-4273-b1fa-e6041a60ac09', '7a230c24-f1c7-414c-b833-d4391dc7f0f4', '1783731961134'),
    s('0107f895-4cc6-45f8-9116-ca3853de2f4c', 'c4121c05-3493-4178-bea0-618a39ffe31e', '1783738562027'),
    s('7a784b9d-1209-4e6d-a7af-7453c22752a4', 'b99e256e-31ea-4f22-a975-6176f56226f1', '1783739793843'),
  ],
  'Adventurous': [
    s('ed0ccb49-81c6-4c03-9579-9f7e682781ee', '109dca48-0443-49cb-b8a2-bf81ea91317c', '1783732016425'),
    s('08f20ae3-e549-4e62-9f60-063a5cd925d7', '6ca0070b-dfea-4d19-bcb4-6836df7a0fd2', '1783738564140'),
    s('f653d6b4-d85b-4810-96c8-d60e0ec9d92e', '76f59544-ce48-4c43-9512-9e1c7cf6680c', '1783739795754'),
  ],
  'Fierce': [
    s('76964b4e-f7ee-4faf-ae2b-9c2faca3353d', '82f692c1-5309-42ea-bc47-500f8594e47f', '1783732091414'),
    s('30ec89e3-1cc0-4d33-96a5-b441541c2f0f', '85a6f594-cce0-4146-80fd-ba094fa972f9', '1783739798088'),
    s('5ef15ca7-0716-4838-82a7-ae3bbfdc1688', '0cf961cc-d2cb-4bdd-8847-aafe25dbc328', '1783739799941'),
  ],
  'Majestic': [
    s('149b9ebe-8484-423f-8f2a-3e23e146a91c', '3c865e0c-c98d-48f1-b4e6-fdccade5eb5b', '1783732135171'),
    s('e08e25bc-ccbe-4b21-9ef6-4381c585f15b', 'df382af2-8d49-4790-ac9d-b0435c76fccc', '1783739982158'),
    s('afacd501-70d0-43b0-a8a8-916550d277c5', '4aeda268-9e92-462c-8594-05f50f9dcd38', '1783739984203'),
  ],
  // ── Dreamy ────────────────────────────────────────────────────────────────
  'Romantic': [
    s('4de9a79f-af1c-4aa4-beea-e6eafedb25e4', 'ddeb4591-da10-46d4-8813-1c0c988dbac4', '1783736611691'),
    s('35a89d3b-f8da-48a5-8be3-5f70eeee100e', 'd2b7fc82-8fc6-4db6-8bd9-74d135b7d630', '1783738567165'),
    s('05b64211-80bb-4cfc-851e-6e06c5080985', 'bafbb1f3-1dab-4bee-b695-b5de0b10dd92', '1783739986926'),
  ],
  'Dreamy': [
    s('830d45c1-d5dc-4d7a-a16e-711016e75236', '4b2fd723-2895-414b-a44e-01e0c215702f', '1783735701930'),
    s('836e4481-03e0-402b-b85c-a59d309fa8cd', 'e688d2a5-d83e-46d7-8344-e7dd190b57f1', '1783739802227'),
    s('091de48b-308e-4c49-90a1-0a11294b1cfd', '1434f1b7-4a83-4269-9e07-f03ff51df595', '1783739993129'),
  ],
  'Enchanted': [
    s('cc67b86b-7d56-4190-b803-3c3b8750d752', 'f6fd708e-b15e-42c2-95bf-748128f5ca3b', '1783735692754'),
    s('c308a7f2-c743-4ee0-a648-c62698317089', 'b313c294-6498-4700-a166-be39a197b68e', '1783739995549'),
    s('63bb9ecc-6159-40f5-922f-2025844d3c1e', 'e5d06e49-3299-4e1e-8935-d30feb01fd48', '1783739997597'),
  ],
  // ── Striking ──────────────────────────────────────────────────────────────
  'Whimsical': [
    s('6486c7a6-c17f-403f-b8a6-c6358c3f2e28', '350f6cbf-d15c-400b-ad76-b5b422546a40', '1783735701971'),
    s('be62237e-88c1-41a5-85ab-39709fdc3899', 'ccf11371-d001-4d92-8b6c-53f1edfe7008', '1783740171297'),
    s('62727c8e-fc07-4ffb-b504-91426690592b', '4cca0a3b-6c9b-426d-9853-ce05eba27762', '1783740174939'),
  ],
  'Bold and modern': [
    s('fc520e58-6903-46b3-be43-e00a28251bd6', '984932dc-3ba9-4c5e-ad7d-00c657e8eff5', '1783735689807'),
    s('1094313b-3bbb-4716-b608-4245e09d4753', '9fe1bc32-8ad0-43dc-ad6c-331d33242288', '1783738570005'),
    s('87a6896f-39f1-414b-98e7-7ae4ca2dc2de', 'a1a1189d-1666-437b-b5f6-c1d0188113bb', '1783740177337'),
  ],
  'Moody': [
    s('6b40ee71-1230-4078-a00f-e2f476bb40ec', '901cefd5-41b4-42bb-be1f-5c3858e059fc', '1783735701888'),
    s('c0d69d5e-dbd7-4b1b-a854-e10ab038c8f6', 'fdd7c860-845c-4711-98c5-bce217a395fd', '1783740000034'),
    s('e3e2b204-418b-491c-bde7-da65b8108491', '1cfd194b-fa5d-44e7-ab4a-d79a3644732f', '1783740179057'),
  ],
  'Dark and gothic': [
    s('e9db1ef7-1efe-4309-908a-c113c72ac76c', '6c39683e-4610-4113-ab32-1dc993b89a1c', '1783735692230'),
    s('503c05d7-42f4-482a-b07d-18850bdaf245', 'dd3bf693-d638-4c49-bd4b-3c1d4866dcd7', '1783740181032'),
    s('65e49e16-c27b-4678-8898-b10139bb6689', '0a507c91-7f40-4674-b432-1bf6dfa85264', '1783740182932'),
  ],
  'Rebellious': [
    s('c4c5ba82-a123-4d77-84e6-a70cb1c8c8fa', '21aeafe2-1b55-4365-93fc-2166ab94541c', '1783735689692'),
    s('6758ed84-e74b-4220-82be-4f3c68362909', '98be03ee-a339-4cae-a3a3-7749d55ed2a5', '1783738572576'),
    s('8a777ec5-6c82-4908-b55d-c0474b33c28a', 'e6ff0a11-4fe4-4932-ad1c-fb31a69c082e', '1783740317614'),
  ],
};

/** Sample image URLs for a feeling (empty array when none are curated yet). */
export function samplesForFeeling(name) {
  const list = FEELING_SAMPLES[name];
  return Array.isArray(list) ? list : [];
}
